import React from 'react';
import Logout from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  Avatar,
  Box,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useSiteContext } from 'src/store';
import clearClientStorage from 'src/utilities/storage/clearClientStorage';
import theme from '../../themes';

interface AvatarProps {
  avatarText?: string;
}

const menuWidth = 230;

const StyledIconButton = styled(IconButton)(() => ({
  width: theme.spacing(3.75),
  height: theme.spacing(3.75),
  marginLeft: theme.spacing(),
  '&:focus': {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
  },
}));

const StyledAvatar = styled(Avatar)(() => ({
  '&.MuiAvatar-root': {
    backgroundColor: theme.palette.primary.main,
    width: theme.spacing(3.75),
    height: theme.spacing(3.75),

    fontSize: theme.typography.fontSize,
    '& .MuiSvgIcon-root': {
      fill: theme.palette.primary.contrastText,
    },
  },
}));

const StyledListItem = styled(ListItem)(() => ({
  padding: theme.spacing(1.5, 0.5),
}));

const StyledListIcon = styled(ListItemIcon)(() => ({
  color: theme.palette.primary.main,
  '& svg': {
    width: theme.spacing(7.5),
  },
}));

const StyledListText = styled(ListItemText)(() => ({
  color: theme.palette.primary.main,
  paddingLeft: theme.spacing(0.5),
  '& span': {
    fontSize: theme.typography.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const AccountMenu = (props: AvatarProps) => {
  const { avatarText } = props;
  const { t } = useTranslation();
  const { authenticationSettings } = useSiteContext();
  const router = useRouter();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = () => {
    handleClose();
  };

  const handleLogout = async () => {
    handleItemClick();
    const response = await axios.get('/api/auth/logout');
    if (response.status === 200) {
      // OTT-1988 (PT-07): drop browser-side state before navigating, so no user
      // PII outlives the session on a shared browser. Must run before the
      // navigation — clearing after one has begun is a race.
      clearClientStorage();
      if (authenticationSettings?.isEnabled) {
        // A client-side router.push would find the sessionStorage `site` blob
        // just cleared and render a 404, so force a full document load to
        // re-run the server branch of MyApp.getInitialProps. The locale prefix
        // is explicit because an unprefixed path falls through to middleware's
        // NEXT_LOCALE-cookie / package-default lookup, discarding the locale
        // the user was actually on.
        const localePrefix =
          router.locale && router.locale !== '__default'
            ? `/${router.locale}`
            : '';
        window.location.assign(`${localePrefix}/signed-out`);
      } else {
        // router.reload() is window.location.reload() — already a full document
        // load, and it keeps the current URL's locale prefix for free.
        router.reload();
      }
    }
  };

  return (
    <>
      <Box sx={{ paddingRight: theme.spacing(1) }} data-testid="account-menu">
        <StyledIconButton
          onClick={handleClick}
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <StyledAvatar>{avatarText ?? <PersonIcon />}</StyledAvatar>
        </StyledIconButton>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            width: menuWidth,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 17px rgba(0,0,0,0.14))',
            mt: 1.5,
            borderRadius: '5px',
            '& .MuiList-root': {
              padding: 0,
            },
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={handleItemClick}
          component={Link}
          href="/account/edit-profile"
        >
          <StyledListItem>
            <StyledListIcon>
              <SettingsOutlinedIcon />
            </StyledListIcon>
            <StyledListText primary={t('header.sharedLinks.myAccount')} />
          </StyledListItem>
        </MenuItem>
        <MenuItem onClick={handleLogout} component={Box}>
          <StyledListItem>
            <StyledListIcon>
              <Logout />
            </StyledListIcon>
            <StyledListText primary={t('header.sharedLinks.logout')} />
          </StyledListItem>
        </MenuItem>
      </Menu>
    </>
  );
};

export default AccountMenu;
